/*
  This provider manages the state for the Frequently Asked Questions. It doesn’t load the questions themselves, since this is a Client Component and we need to do that on the server. Instead, it keeps track of which FAQs are open in a map-like object, keyed by slug.
*/
'use client';

import * as React from 'react';

export const FaqContext = React.createContext<{
  relevantMatches: Array<string>;
  openItems: Record<string, boolean>;
  setRelevantMatches: React.Dispatch<
    React.SetStateAction<Array<string>>
  >;
  toggleItem: (slug: string) => void;
}>({
  openItems: {},
  toggleItem: () => {},
  relevantMatches: [],
  setRelevantMatches: () => {},
});

function FaqProvider({ children }: { children: React.ReactNode }) {
  const [openItems, setOpenItems] = React.useState<
    Record<string, boolean>
  >({});

  const [relevantMatches, setRelevantMatches] = React.useState<
    Array<string>
  >([]);

  const toggleItem = React.useCallback((slug: string) => {
    setOpenItems((currentValue: Record<string, boolean>) => ({
      ...currentValue,
      [slug]: !currentValue[slug],
    }));
  }, []);

  React.useLayoutEffect(() => {
    const { hash } = window.location;
    const match = hash.match(/faq-(.+)/);

    if (match) {
      const [, slug] = match;
      toggleItem(slug);

      window.requestAnimationFrame(() => {
        const elem = document.getElementById(`faq-${slug}`);
        if (elem) {
          elem.scrollIntoView({ behavior: 'smooth' });
        }
      });
    }
  }, [toggleItem]);

  const contextValue = React.useMemo(() => {
    return {
      openItems,
      toggleItem,
      relevantMatches,
      setRelevantMatches,
    };
  }, [openItems, toggleItem, relevantMatches, setRelevantMatches]);

  return (
    <FaqContext.Provider value={contextValue}>
      {children}
    </FaqContext.Provider>
  );
}

export default React.memo(FaqProvider);
